import { useState } from "react";
import { X, Plus } from "lucide-react";
import {Button} from "@/components/ui/button";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface GlossaryIdentifierProps {
  identifiers: string[];
  onIdentifiersChange: (identifiers: string[]) => void;
  mode: "create" | "edit" | "view";
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const GlossaryIdentifier = ({
  identifiers,
  onIdentifiersChange,
  mode,
}: GlossaryIdentifierProps) => {
  const { t } = usePlatformTranslation();
  const [newIdentifier, setNewIdentifier] = useState("");

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleAddIdentifier = () => {
    if (newIdentifier.trim() && !identifiers.includes(newIdentifier.trim())) {
      const updatedIdentifiers = [...identifiers, newIdentifier.trim()];
      onIdentifiersChange(updatedIdentifiers);
      setNewIdentifier("");
    }
  };

  const handleRemoveIdentifier = (identifierToRemove: string) => {
    const updatedIdentifiers = identifiers.filter(
      (identifier) => identifier !== identifierToRemove
    );
    onIdentifiersChange(updatedIdentifiers);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddIdentifier();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-foreground">
        Identifiers
      </label>
      
      {/* Add new identifier input */}
      {(mode === "create" || mode === "edit") && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newIdentifier}
            onChange={(e) => setNewIdentifier(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('platform.wizards.glossary.add_an_identifier')}
            className="flex-1 px-3 py-2 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          <Button
            onClick={handleAddIdentifier}
            disabled={!newIdentifier.trim()}
            variant="ghost"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Identifiers list */}
      {identifiers.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {identifiers.map((identifier, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1 bg-card text-foreground rounded-full text-sm border border-border"
            >
              <span>{identifier}</span>
              <Button
                onClick={() => handleRemoveIdentifier(identifier)}
                variant="ghost"
                size="badge"
                title={t('platform.wizards.glossary.remove_identifier')}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground italic">
          No identifiers added yet
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-muted-foreground">
        {t('platform.wizards.glossary.identifiers_help_text')}
        
    
      </p>
    </div>
  );
};
